/**
 * Token utility functions for JWT token management
 */

import {
  clearImpersonationSession,
  getImpersonationSession,
  isImpersonating,
  updateImpersonationTokens,
} from "./session-utils";

interface JWTPayload {
  exp?: number;
  iat?: number;
  user_id?: string;
  email?: string;
  tenant_id?: string;
  role?: string;
}

/**
 * Decode JWT token without verification (client-side only)
 * Note: This does NOT verify the signature - only extracts the payload
 */
export function decodeJWT(token: string): JWTPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    
    // Decode the payload (second part)
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as JWTPayload;
  } catch (error) {
    console.error('Failed to decode JWT token:', error);
    return null;
  }
}

/**
 * Check if a token is expired or will expire soon
 * @param token - JWT token string
 * @param bufferSeconds - Number of seconds before expiry to consider token as "expiring soon" (default: 60 seconds)
 * @returns Object with isExpired, isExpiringSoon, and expiresAt
 */
export function checkTokenExpiry(token: string | null, bufferSeconds: number = 60): {
  isExpired: boolean;
  isExpiringSoon: boolean;
  expiresAt: number | null;
} {
  if (!token) {
    return { isExpired: true, isExpiringSoon: true, expiresAt: null };
  }

  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    return { isExpired: true, isExpiringSoon: true, expiresAt: null };
  }

  const expiresAt = payload.exp * 1000; // Convert to milliseconds
  const now = Date.now();
  const bufferMs = bufferSeconds * 1000;
  
  const isExpired = expiresAt <= now;
  const isExpiringSoon = expiresAt <= (now + bufferMs);

  return {
    isExpired,
    isExpiringSoon,
    expiresAt,
  };
}

/**
 * Get the access token from localStorage
 */
export function getAccessToken(): string | null {
  const impersonationSession = getImpersonationSession();
  if (impersonationSession) {
    return impersonationSession.accessToken;
  }

  return localStorage.getItem('authToken');
}

/**
 * Get the refresh token from localStorage
 */
export function getRefreshToken(): string | null {
  const impersonationSession = getImpersonationSession();
  if (impersonationSession) {
    return impersonationSession.refreshToken;
  }

  return localStorage.getItem('refreshToken');
}

/**
 * Save tokens to localStorage
 */
export function saveTokens(accessToken: string, refreshToken: string): void {
  if (isImpersonating()) {
    updateImpersonationTokens(accessToken, refreshToken);
    return;
  }

  localStorage.setItem('authToken', accessToken);
  localStorage.setItem('refreshToken', refreshToken);
}

/**
 * Clear tokens from localStorage
 */
export function clearTokens(): void {
  sessionStorage.removeItem('pendingSignupSource');
  if (isImpersonating()) {
    clearImpersonationSession();
    sessionStorage.removeItem('pendingGoogleIdToken');
    return;
  }

  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  sessionStorage.removeItem('pendingGoogleIdToken');
}
